import { type UserGroupType } from '@navio/nsdk';
import { useStore } from '@tanstack/react-form';
import { invalidateQuery, invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiButton, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { CompanyLookupSummary } from '../CompanyLookup/CompanyLookupSummary';
import { normalizeOrganizationNumber, useCompanyLookup } from '../CompanyLookup/useCompanyLookup';
import { useCanConvertToOrganization } from './useCanConvertToOrganization';

const FORM_ID = 'change-to-organization-form';
const schema = z.object({
  organizationNumber: z
    .string()
    .refine((value) => /^\d{9}$/.test(normalizeOrganizationNumber(value)), 'Enter a 9-digit organization number.')
});

type Props = {
  open: boolean;
  onClose: () => void;
  userGroupId: number;
  userGroupType: UserGroupType;
  onConverted: (organizationId: number) => void;
};

export const ChangeToOrganizationSheet = ({ open, userGroupType, ...props }: Props) => {
  const canConvert = useCanConvertToOrganization(userGroupType);
  return open && canConvert ? <ConversionForm key={props.userGroupId} {...props} /> : null;
};

const ConversionForm = ({ onClose, userGroupId, onConverted }: Omit<Props, 'open' | 'userGroupType'>) => {
  const { mutateAsync: convertToOrganization, error: convertError } = noddiAsync.usePost({
    type: URLKeys.postUserGroupConvertToOrganization,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => {
        void invalidateQueryExactMatch({ urlKey: URLKeys.getUserGroup, input: { userGroupId } });
        void invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroups });
        void invalidateQuery({ urlKey: URLKeys.getPaginatedOrganizations });
        void invalidateQuery({ urlKey: URLKeys.getPaginatedDepartments });
        void invalidateQuery({ urlKey: URLKeys.getUserGroupFilterCounts });
      }
    }
  });
  const form = useAppForm({
    defaultValues: { organizationNumber: '' },
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      // Also guard keyboard submission; lookup data belongs to the current query key.
      if (
        !companyData ||
        normalizeOrganizationNumber(companyData.organizationNumber) !==
          normalizeOrganizationNumber(value.organizationNumber)
      ) {
        return;
      }
      try {
        await convertToOrganization(
          { userGroupId, registrationNumber: normalizeOrganizationNumber(value.organizationNumber) },
          {
            onSuccess: (response) => {
              onConverted(response.data.id);
              onClose();
            }
          }
        );
      } catch {
        // The sheet keeps the backend refusal visible until the next attempt.
      }
    }
  });
  const organizationNumber = useStore(form.store, (state) => state.values.organizationNumber);
  const {
    data: companyData,
    isFetching,
    error: lookupError,
    refetch,
    canLookup
  } = useCompanyLookup(organizationNumber);
  const disabledReason = !canLookup
    ? 'Enter a 9-digit organization number'
    : !companyData
      ? 'Look up the organization number before saving'
      : undefined;

  return (
    <form.AppForm>
      <NoddiSheet
        open
        onOpenChange={(next) => !next && onClose()}
        title='Change to organization'
        description='Look up the company in the registry. This customer becomes that organization. Members, cars, and bookings stay.'
        footer={
          <form.SubscribeButton
            formId={FORM_ID}
            label='Change to organization'
            className='w-full'
            disabledReason={disabledReason}
          />
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppField name='organizationNumber'>
            {(field) => <field.Input label='Organization number' />}
          </form.AppField>
          <NoddiButton
            variant='outline'
            size='small'
            className='self-start'
            loading={isFetching}
            disabled={!canLookup}
            onClick={() => void refetch()}
          >
            Look up
          </NoddiButton>
          {lookupError && (
            <div role='alert'>
              <ApiErrorMessageInternal error={lookupError} />
            </div>
          )}
          {companyData && (
            <>
              <CompanyLookupSummary companyData={companyData} />
              <p className='text-sm text-muted-foreground'>Customer name becomes {companyData.name}.</p>
            </>
          )}
          {convertError && (
            <div role='alert'>
              <ApiErrorMessageInternal error={convertError} />
            </div>
          )}
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
